/**
 * EWM-949 — the upload file-type gate, shared by the two independent uploaders:
 * the "Order Upload" drawer (`organisms/quick-order-upload`) and the "Quick
 * Order" desktop panel (`organisms/quick-order/.../quick-order-csv-upload`).
 *
 * They had diverged. The drawer accepted three MIME types plus an extension
 * fallback; the panel accepted `text/csv` and nothing else — so the same .csv
 * was accepted by one uploader and rejected by the other, because Windows and
 * several browsers report `application/vnd.ms-excel` (and sometimes an empty
 * string) for a plain .csv. The extension is the more reliable signal, which is
 * why it is a fallback rather than an additional requirement.
 */
export const ACCEPTED_UPLOAD_TYPES = new Set([
  'text/csv',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
]);

export const hasValidUploadExtension = (name: string) => /\.(csv|xlsx|xls)$/i.test(name);

/** True when the file is plausibly an order upload, by reported type OR by extension. */
export const isAcceptedUploadFile = (file: File) =>
  ACCEPTED_UPLOAD_TYPES.has(file.type) || hasValidUploadExtension(file.name);

const CSV_EXTENSION = /\.csv$/i;

/**
 * EWM-949 round 1 (R1-5) — the NARROWER gate, for the surfaces that read the
 * file as text and parse the rows themselves: the Quick Order desktop panel,
 * and the drawer's client-side parse.
 *
 * Those surfaces cannot read a spreadsheet at all (XLS/XLSX parsing is
 * server-side, pending EWM-942/EWM-247), so admitting one through the broader
 * gate above is a false promise: the panel `readAsText`s a binary blob and
 * lands on the generic "please review template file" instead of saying, up
 * front, that it needs a CSV.
 *
 * Extension-first on purpose — Windows reports `application/vnd.ms-excel` for
 * a plain .csv AND for a real .xls, so the type alone cannot separate them.
 */
export const isCsvUploadFile = (file: File) => CSV_EXTENSION.test(file.name) || file.type === 'text/csv';
